import React from 'react';
import { useRiskSnapshots, useRiskMatrix } from '../hooks/useQueries';
import LoadingSpinner from './Popup/LoadingSpinner';
import RiskScoreLineChart from './RiskScoreLineChart';
import PopupMenu from './Popup/PopupMenu';
import { usePopupMenu } from '../hooks/usePopupMenu';
import './RiskAssessmentTimeline.css';

const RiskAssessmentTimeline = ({ itemId, onDeleteSnapshot }) => {
  const { data: snapshots, isLoading, error } = useRiskSnapshots(itemId);
  const { data: riskMatrixConfig } = useRiskMatrix();

  const popupMenu = usePopupMenu();

  // Helper function to get risk zone name from score using matrix config
  const getRiskZoneName = (score) => {
    if (!score || isNaN(score) || !riskMatrixConfig?.zone_thresholds) return 'Unknown';
    
    const numScore = parseInt(score);
    
    // Check each zone threshold to find which zone this score belongs to
    for (const [zoneName, threshold] of Object.entries(riskMatrixConfig.zone_thresholds)) {
      if (threshold && threshold.min !== undefined && threshold.max !== undefined &&
          numScore >= threshold.min && numScore <= threshold.max) {
        return zoneName;
      }
    }
    
    return 'Unknown';
  };

  // Helper function to get risk score color based on zone name
  const getRiskScoreColor = (score) => {
    const zoneName = getRiskZoneName(score);
    
    // Map zone names to CSS classes - handle case insensitive and normalize
    const normalizedZone = zoneName.toLowerCase().replace(/\s+/g, '-');
    
    const zoneColorMap = {
      'low': 'risk-zone-low',
      'medium': 'risk-zone-medium', 
      'high': 'risk-zone-high',
      'very-high': 'risk-zone-very-high',
      'unknown': 'risk-zone-unknown'
    };
    
    return zoneColorMap[normalizedZone] || 'risk-zone-unknown';
  };

  // Helper function to calculate delta between consecutive snapshots
  const calculateDelta = (currentScore, previousScore) => {
    if (!currentScore || !previousScore) return null;
    return parseInt(currentScore) - parseInt(previousScore);
  };

  // Helper function to format delta display
  const formatDelta = (delta) => {
    if (delta === null || delta === 0) return { text: '=', class: 'delta-neutral' };
    if (delta > 0) return { text: `↑${delta}`, class: 'delta-increase' };
    return { text: `↓${Math.abs(delta)}`, class: 'delta-decrease' };
  };

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return timestamp;
    }
  };

  const formatSnapshotType = (snapshot) => {
    const type = snapshot?.snapshot_type;
    if (!type) return 'Snapshot';

    if (type === 'stage_transition') {
      const approver = snapshot?.captured_by;
      return approver ? `Stage Transition - Approved by ${approver}` : 'Stage Transition';
    }

    // Title-case other types like manual, post_mitigation, etc.
    return type
      .split('_')
      .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
      .join(' ');
  };
  
  const handleDeleteClick = (snapshot) => {
    onDeleteSnapshot(snapshot);
    popupMenu.closePopupMenu();
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="risk-timeline-container">
        <h4>Risk Assessment Timeline</h4>
        <div className="error-message">
          Error loading risk history: {error.message}
        </div>
      </div>
    );
  }

  if (!snapshots || snapshots.length === 0) {
    return (
      <div className="risk-timeline-container">
        <h4>Risk Assessment Timeline</h4>
        <div className="no-snapshots">
          No risk assessment snapshots available for this item.
        </div>
      </div>
    );
  }

  // Calculate summary statistics
  const firstSnapshot = snapshots[0];
  const lastSnapshot = snapshots[snapshots.length - 1];
  const firstScore = parseInt(firstSnapshot.risk_score);
  const lastScore = parseInt(lastSnapshot.risk_score);
  const overallTrend = lastScore > firstScore ? 'worsened' : lastScore < firstScore ? 'improved' : 'stable';

  return (
    <div className="risk-timeline-container">
      <h4>Risk Assessment Timeline</h4>
      
      <RiskScoreLineChart snapshots={snapshots} riskMatrixConfig={riskMatrixConfig} />
      
      <div className="risk-timeline">
        {snapshots.map((snapshot, index) => {
          const previousSnapshot = index > 0 ? snapshots[index - 1] : null;
          const delta = previousSnapshot ? calculateDelta(snapshot.risk_score, previousSnapshot.risk_score) : null;
          const deltaDisplay = formatDelta(delta);

          return (
            <div key={snapshot.id} className="timeline-item">
              <div className="timeline-left">
                <div className={`risk-score-badge ${getRiskScoreColor(snapshot.risk_score)}`}>
                  {snapshot.risk_score}
                </div>
              </div>
              
              <div className="timeline-content">
                <div className="timeline-header">
                  <div className="timeline-header-left">
                    <div className="stage-name">{snapshot.stage_name || 'Unknown Stage'}</div>
                    <div className="snapshot-type">{formatSnapshotType(snapshot)}</div>
                  </div>
                  <div className="timeline-header-right">
                    <div className="dropdown-container">
                      <button 
                        className="dropdown-toggle"
                        data-popup-trigger
                        onClick={(e) => popupMenu.handleItemClick(e, snapshot)}
                        aria-label="Snapshot actions"
                      >
                        <i className="bi bi-three-dots-vertical"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="risk-details">
                  <span>Zone: {getRiskZoneName(snapshot.risk_score)} ({snapshot.risk_score || '?'})</span>
                  <span>Probability: {snapshot.probability_score || 'N/A'}</span>
                  <span>Impact: {snapshot.threat_score || 'N/A'}</span>
                  {delta !== null && (
                    <span className={`risk-delta ${deltaDisplay.class}`}>
                      {deltaDisplay.text}
                    </span>
                  )}
                </div>
                {snapshot.notes && (
                  <div className="snapshot-notes">{snapshot.notes}</div>
                )}
              </div>
              
              <div className="timeline-meta">
                <div className="timestamp">{formatTimestamp(snapshot.captured_at)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {popupMenu.popupMenu.show && (
        <PopupMenu
          x={popupMenu.popupMenu.x}
          y={popupMenu.popupMenu.y}
          title="Snapshot"
          onClose={popupMenu.closePopupMenu}
          items={[
            {
              label: 'Delete Snapshot',
              icon: 'bi bi-trash',
              color: '#dc2626',
              onClick: () => handleDeleteClick(popupMenu.popupMenu.item)
            }
          ]}
        />
      )}

      <div className="risk-timeline-summary">
        <div className="summary-item">
          <strong>{snapshots.length}</strong> snapshots recorded
        </div>
        <div className="summary-item">
          From {formatTimestamp(firstSnapshot.captured_at)} to {formatTimestamp(lastSnapshot.captured_at)}
        </div>
        <div className="summary-item">
          Risk trend: <span className={`trend-${overallTrend}`}>{overallTrend}</span>
        </div>
      </div>
    </div>
  );
};

export default RiskAssessmentTimeline;